import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { nanoid } from '@reduxjs/toolkit'
import { produce } from 'immer'
import { noop } from 'utils'

export const noneTime =
  '000000000000000000000000 000000000000000000000000 000000000000000000000000 000000000000000000000000 000000000000000000000000 000000000000000000000000 000000000000000000000000'
export const everyTime =
  '111111111111111111111111 111111111111111111111111 111111111111111111111111 111111111111111111111111 111111111111111111111111 111111111111111111111111 111111111111111111111111'
export const toInt = bit =>
  bit
    .split(' ')
    .map(item => parseInt(item, 2))
    .join(' ')
export const toBit = int =>
  int
    .split(' ')
    .map(item => parseInt(item, 10).toString(2).padStart(24, '0'))
    .join(' ')
export const ScheduleCell = ({
  day,
  hour,
  checkedList,
  activeList,
  onChange = noop,
}) => {
  const idRef = useRef(nanoid())
  const checked = checkedList.some(
    item => item.day === day && item.hour === hour
  )
  const active = activeList.some(item => item.day === day && item.hour === hour)
  const [hover, setHover] = useState(false)
  return (
    <td
      className={classNames({
        on: checked,
        over: active || hover,
      })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      <div className="check_sch">
        <input
          type="checkbox"
          id={idRef.current}
          className="inp_check"
          value={JSON.stringify({
            day,
            hour,
          })}
          checked={checked}
          onChange={onChange}
        />
        <label htmlFor={idRef.current} className="lab_option">
          <span className="screen_out">
            {day} {hour}
          </span>
        </label>
      </div>
    </td>
  )
}
ScheduleCell.propTypes = {
  day: PropTypes.string.isRequired,
  hour: PropTypes.number.isRequired,
  checkedList: PropTypes.arrayOf(
    PropTypes.exact({
      day: PropTypes.string,
      hour: PropTypes.number,
    })
  ).isRequired,
  activeList: PropTypes.arrayOf(
    PropTypes.exact({
      day: PropTypes.string,
      hour: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func,
}

export const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
export const koreanMap = {
  mon: '월',
  tue: '화',
  wed: '수',
  thu: '목',
  fri: '금',
  sat: '토',
  sun: '일',
}
export const parse = text => {
  const splits = text.split(' ')
  let result = []
  splits.forEach((bit, dayIndex) => {
    const schedule = [...bit].reduce((accumulator, value, index) => {
      if (value === '1') {
        accumulator.push({
          day: days[dayIndex],
          hour: index,
        })
      }
      return accumulator
    }, [])
    result = [...result, ...schedule]
  })
  return result
}
export const generateHours = day =>
  [...Array(24).keys()].map(hour => ({
    hour,
    day,
  }))
export const generateDays = hour =>
  days.map(day => ({
    hour,
    day,
  }))

const width = 76
const height = 25
const convertValue = (x, y) => ({
  day: days[Math.floor(x / width)],
  hour: Math.min(Math.floor(y / height), 23),
})
const TimeTable = ({
  weeklySchedule = everyTime,
  onChange,
  error,
  headerWidth = 78,
}) => {
  const wrapRef = useRef()
  const dragRef = useRef({
    start: null,
    end: null,
  })
  const [draggable, setDraggable] = useState(false)
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  })
  const [checkedList, setCheckedList] = useState(parse(weeklySchedule))
  const [activeList, setActiveList] = useState([])
  const handleDaysActive = day => {
    setActiveList(generateHours(day))
  }
  const handleHoursActive = hour => {
    setActiveList(generateDays(hour))
  }
  const clearActive = () => {
    setActiveList([])
  }
  const getOffsetPosition = (pageX, pageY) => {
    const boundingClientRect = wrapRef.current.getBoundingClientRect()
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const offset = {
      x: pageX - boundingClientRect.left - scrollLeft - headerWidth,
      y: pageY - boundingClientRect.top - scrollTop - height,
    }
    return offset.x > 0 && offset.y > 0 ? offset : null
  }
  const getBoundary = () => {
    const { start, end } = dragRef.current
    const { day: startDay, hour: startHour } = convertValue(start.x, start.y)
    const { day: endDay, hour: endHour } = convertValue(end.x, end.y)
    return {
      minDay: Math.min(days.indexOf(startDay), days.indexOf(endDay)),
      maxDay: Math.max(days.indexOf(startDay), days.indexOf(endDay)),
      minHour: Math.min(startHour, endHour),
      maxHour: Math.max(startHour, endHour),
    }
  }
  const handleMouseUp = event => {
    event.preventDefault()
    const { start, end } = dragRef.current
    if (start && end) {
      const { minDay, maxDay, minHour, maxHour } = getBoundary()
      const filteredList = checkedList.filter(
        item =>
          days.indexOf(item.day) < minDay ||
          days.indexOf(item.day) > maxDay ||
          item.hour < minHour ||
          item.hour > maxHour
      )
      const selectedList = Array.from(
        { length: maxDay - minDay + 1 },
        (v, k) => k + minDay
      )
        .map(item =>
          Array.from({ length: maxHour - minHour + 1 }, (v, k) => ({
            day: days[item],
            hour: k + minHour,
          }))
        )
        .flat()
      const addList = selectedList.filter(
        selected =>
          checkedList.findIndex(
            checked =>
              checked.day === selected.day && checked.hour === selected.hour
          ) === -1
      )
      setCheckedList([...filteredList, ...addList])
    }
    dragRef.current.start = null
    dragRef.current.end = null
    setDraggable(false)
    setPosition({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    })
  }
  const handleMouseDown = event => {
    event.preventDefault()
    const { pageX, pageY } = event
    const offsetPos = getOffsetPosition(pageX, pageY)
    if (offsetPos) {
      setDraggable(true)
      dragRef.current.start = offsetPos
      document.addEventListener('mouseup', handleMouseUp, { once: true })
    }
  }
  const handleMouseMove = event => {
    event.preventDefault()
    const { pageX, pageY } = event
    const { start } = dragRef.current
    if (start) {
      const end = getOffsetPosition(pageX, pageY)
      if (end) {
        dragRef.current.end = end
        const { minDay, maxDay, minHour, maxHour } = getBoundary()
        const dayCount = maxDay - minDay
        const hourCount = maxHour - minHour
        setPosition({
          left: width * minDay + headerWidth - 1,
          top: height * (minHour + 1),
          width: width * (dayCount + 1) + 1,
          height: height * (hourCount + 1) + 1,
        })
      }
    }
  }
  const handleDaysChecked = event => {
    const { target } = event
    const { value, checked } = target
    const filteredList = checkedList.filter(item => item.day !== value)
    if (checked) {
      setCheckedList([...filteredList, ...generateHours(value)])
    } else {
      setCheckedList(filteredList)
    }
  }
  const handleHoursChecked = event => {
    const { target } = event
    const { value, checked } = target
    const hour = Number(value)
    const filteredList = checkedList.filter(item => item.hour !== hour)
    if (checked) {
      setCheckedList([...filteredList, ...generateDays(hour)])
    } else {
      setCheckedList(filteredList)
    }
  }
  const handleChange = event => {
    const { target } = event
    const { checked, value } = target
    const parsedValue = JSON.parse(value)
    if (checked) {
      setCheckedList(
        produce(draft => {
          draft.push(parsedValue)
        })
      )
    } else {
      setCheckedList(
        produce(draft => {
          const index = draft.findIndex(
            item =>
              item.hour === parsedValue.hour && item.day === parsedValue.day
          )
          if (index !== -1) {
            draft.splice(index, 1)
          }
        })
      )
    }
  }
  useEffect(() => {
    const emptyValue = noneTime.split('')
    checkedList.forEach(item => {
      const index =
        days.indexOf(item.day) * 24 + days.indexOf(item.day) + item.hour
      emptyValue[index] = '1'
    })
    const stringify = emptyValue.join('')
    if (weeklySchedule !== stringify) {
      onChange(stringify)
    }
  }, [checkedList, onChange, weeklySchedule])
  return (
    <div
      ref={wrapRef}
      className={classNames('sch_wrap', { in_error: error })}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}>
      <div className="info_sch">
        <span className="sch_choice on">노출</span>
        <span className="sch_choice off">미노출</span>
      </div>
      <table className="tbl_sch">
        <caption className="hide_caption">요일시간 상세설정 테이블</caption>
        <colgroup>
          <col />
          <col style={{ width: 76 }} />
          <col style={{ width: 76 }} />
          <col style={{ width: 76 }} />
          <col style={{ width: 76 }} />
          <col style={{ width: 76 }} />
          <col style={{ width: 76 }} />
          <col style={{ width: 76 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="th_tit">
              <div className="tit_wrap">
                <span className="tit_week">요일</span>
                <span className="tit_week week_time">시간</span>
              </div>
            </th>
            {days.map(day => (
              <th
                key={`${day}CheckHeader`}
                scope="col"
                onMouseEnter={() => handleDaysActive(day)}
                onMouseLeave={clearActive}>
                <div className="check_sch">
                  <input
                    type="checkbox"
                    id={`${day}Check`}
                    className="inp_check"
                    value={day}
                    checked={
                      checkedList.filter(item => item.day === day).length === 24
                    }
                    onChange={handleDaysChecked}
                  />
                  <label htmlFor={`${day}Check`} className="lab_option">
                    <span className="txt_week">{koreanMap[day]}</span>
                  </label>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(24).keys()].map(hour => (
            <tr key={`timeCheck${hour}Header`}>
              <th
                scope="row"
                onMouseEnter={() => handleHoursActive(hour)}
                onMouseLeave={clearActive}>
                <div className="check_sch">
                  <input
                    type="checkbox"
                    id={`timeCheck${hour}`}
                    className="inp_check"
                    value={hour}
                    checked={
                      checkedList.filter(item => item.hour === hour).length ===
                      7
                    }
                    onChange={handleHoursChecked}
                  />
                  <label htmlFor={`timeCheck${hour}`} className="lab_option">
                    {String(hour).padStart(2, '0')}시
                  </label>
                </div>
              </th>
              {days.map(day => (
                <ScheduleCell
                  key={`${day}${hour}`}
                  day={day}
                  hour={hour}
                  checkedList={checkedList}
                  activeList={activeList}
                  onChange={handleChange}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {draggable && <span className="drag_wrap" style={position} />}
    </div>
  )
}

TimeTable.propTypes = {
  weeklySchedule: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.any,
  headerWidth: PropTypes.number,
}

export default TimeTable
